import { useState } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Spinner,
  Tooltip,
  Button,
  Input,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@fluentui/react-components';
import {
  Attach16Regular,
  ChevronRight16Regular,
  MoreHorizontalRegular,
  ArrowDownloadRegular,
} from '@fluentui/react-icons';
import { MailItemIcon } from '@/components/ItemIcon';
import { UnreadBadge } from '@/components/UnreadBadge';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { MailSummary } from '@/types/domain';
import { openMail } from '@/utils/openMail';
import { formatMailDate } from '@/utils/mailDate';
import { useDeleteMail, useRenameMail } from '@/hooks/useMailMutations';
import { useSetMailRead } from '@/hooks/useSetMailRead';
import { useItemClipboard } from '@/hooks/useItemClipboard';
import { ItemActionMenuItems } from '@/components/ItemActionMenuItems';
import { MailReadMenuItem } from '@/components/MailReadMenuItem';
import { menuPositioning, pointTarget, type CursorTarget } from '@/components/cursorMenu';
import { useResolveUser } from '@/hooks/useResolveUser';
import { useRowSelection } from '@/features/multi-select/useRowSelection';
import { useMultiSelectionMenu } from '@/features/multi-select/useMultiSelectionMenu';
import { treeScope, type SelectedRef } from '@/features/multi-select/selection';
import { MailTargetDialog } from './MailTargetDialog';

const useStyles = makeStyles({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `0 ${tokens.spacingHorizontalXS}`,
    minHeight: '30px',
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusSmall,
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover },
  },
  dragging: { opacity: 0.5 },
  // Sélection multiple. Le MÊME fond que le dossier actif (`FolderRow.rowActive`)
  // et que la ligne sélectionnée de la page détail : trois teintes différentes
  // pour « cette ligne est retenue » se liraient comme trois états distincts.
  selected: { backgroundColor: tokens.colorBrandBackground2 },
  unreadSubject: { color: tokens.colorBrandForeground1, fontWeight: tokens.fontWeightSemibold },
  // Mails have no expand chevron. Without compensation their icon lands in the
  // parent folder's chevron column, so a mail filed inside a folder reads as a
  // sibling of that folder. Reserve the chevron button's exact footprint (a
  // hidden clone, same pattern as FolderRow.chevronSpacer) so a mail aligns with
  // its sibling sub-folders — one clear step to the right of the folder name.
  chevronSpacer: { visibility: 'hidden', pointerEvents: 'none', flexShrink: 0 },
  // PAS DE `marginRight` ICI : l'icône est enveloppée par `UnreadBadge`, dont la
  // pastille se repère sur la boîte du conteneur — marges de l'enfant comprises.
  // Une marge de fin de 2 px suffisait à décaler la pastille hors de l'enveloppe.
  // L'écart avec le libellé est celui de la ligne (`gap`), comme partout ailleurs.
  icon: { flexShrink: 0, color: tokens.colorNeutralForeground3 },
  subject: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase300,
  },
  renameInput: { flex: 1 },
  attach: { flexShrink: 0, color: tokens.colorNeutralForeground3 },
  actions: { display: 'flex', alignItems: 'center', flexShrink: 0 },
  hidden: { display: 'none' },
  visible: {},
  tip: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, maxWidth: '260px' },
  tipDim: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
});

interface Props {
  mail: MailSummary;
  depth: number;
}

export function MailRow({ mail, depth }: Props) {
  const styles = useStyles();
  const [opening, setOpening] = useState(false);
  const [hover, setHover] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuTarget, setMenuTarget] = useState<CursorTarget | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(mail.displayName ?? mail.subject ?? '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [targetMode, setTargetMode] = useState<'copy' | 'move' | null>(null);
  const [tipOpen, setTipOpen] = useState(false);

  const deleteMut = useDeleteMail();
  const renameMut = useRenameMail();
  const setRead = useSetMailRead();
  const { canPaste, pending, isBusy, copyMails, cutMails, pasteInto } = useItemClipboard();

  const sel = useRowSelection(treeScope(mail.containerId));
  const selected = sel.isSelected(mail.id);
  const multi = useMultiSelectionMenu({
    items: sel.items,
    onDone: () => setMenuOpen(false),
  });

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `mail:${mail.id}`,
    data: { type: 'mail', mailId: mail.id, folderId: mail.folderId, containerId: mail.containerId },
  });

  const label = (mail.displayName || mail.subject || '(sans objet)').trim() || '(sans objet)';
  // Le PARENT d'un mail est son dossier — `null` quand il est rangé à la racine de
  // l'espace. C'est ce qui permet de ne pas le déplacer là où il est déjà, et de
  // repérer qu'un dossier sélectionné l'emporte déjà.
  const ref: SelectedRef = {
    kind: 'mail',
    id: mail.id,
    containerId: mail.containerId,
    parentId: mail.folderId,
    label,
  };
  const needResolve = !mail.filedByName && Boolean(mail.filedBy);
  const { data: resolvedName } = useResolveUser(mail.filedBy, tipOpen && needResolve);
  const filer = mail.filedByName ?? resolvedName ?? null;
  const mailDate = formatMailDate(mail.receivedAt ?? mail.sentAt);

  const tip = (
    <div className={styles.tip}>
      {mailDate && <span>{mailDate}</span>}
      {filer && <span className={styles.tipDim}>Rangé par {filer}</span>}
      <span className={styles.tipDim}>Rangé le {formatMailDate(mail.filedAt)}</span>
    </div>
  );

  const open = async () => {
    if (opening) return;
    setOpening(true);
    setRead(mail.id, true);
    try {
      await openMail(mail.id, label);
    } finally {
      setOpening(false);
    }
  };

  const startRename = () => {
    setRenameValue(mail.displayName ?? mail.subject ?? '');
    setRenaming(true);
    setMenuOpen(false);
  };
  const submitRename = () => {
    const next = renameValue.trim();
    const current = (mail.displayName ?? mail.subject ?? '').trim();
    if (next === current) {
      setRenaming(false);
      return;
    }
    renameMut.mutate(
      { mailId: mail.id, folderId: mail.folderId, displayName: next },
      { onSettled: () => setRenaming(false) },
    );
  };

  return (
    <>
      <div
        ref={setNodeRef}
        className={`${styles.row} ${isDragging ? styles.dragging : ''} ${selected ? styles.selected : ''}`}
        style={{
          paddingLeft: `calc(${tokens.spacingHorizontalXS} + ${depth * 12}px)`,
          transform: CSS.Translate.toString(transform),
        }}
        aria-selected={selected}
        {...sel.attrs(ref)}
        onClick={(e) => {
          if (renaming) return;
          // CTRL/MAJ+clic est un geste de SÉLECTION : il ne doit pas ouvrir le
          // mail au passage. Un clic simple, lui, ouvre comme toujours.
          if (sel.onRowClick(e, ref)) return;
          void open();
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // Clic droit HORS sélection : elle est abandonnée, comme dans
          // l'explorateur. Sans cela le menu proposerait « Déplacer 4 éléments »
          // depuis une ligne qui n'en fait pas partie.
          if (!selected && sel.items.length > 0) sel.clear();
          setMenuTarget(pointTarget(e.clientX, e.clientY));
          setMenuOpen(true);
        }}
        {...(renaming ? {} : listeners)}
        {...attributes}
      >
        <Button
          appearance="subtle"
          size="small"
          icon={<ChevronRight16Regular />}
          className={styles.chevronSpacer}
          disabled
          tabIndex={-1}
          aria-hidden
        />
        {/* Pastille « non lu ». Présente ICI AUSSI, et pas seulement sur la page
            détail : le même mail apparaît dans les deux vues, et le voir non-lu
            d'un côté et neutre de l'autre serait pire que pas d'indicateur du
            tout. `=== false` strictement (cf. types/domain.ts). */}
        <UnreadBadge unread={mail.isRead === false}>
          {opening ? <Spinner size="tiny" /> : <MailItemIcon className={styles.icon} />}
        </UnreadBadge>
        {renaming ? (
          <Input
            className={styles.renameInput}
            size="small"
            autoFocus
            value={renameValue}
            disabled={renameMut.isPending}
            onChange={(_e, d) => setRenameValue(d.value)}
            onClick={(e) => e.stopPropagation()}
            onBlur={submitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitRename();
              if (e.key === 'Escape') setRenaming(false);
            }}
          />
        ) : (
          <Tooltip
            content={tip}
            relationship="description"
            onVisibleChange={(_e, data) => setTipOpen(data.visible)}
          >
            <span
              className={
                mail.isRead === false
                  ? mergeClasses(styles.subject, styles.unreadSubject)
                  : styles.subject
              }
            >
              {label}
            </span>
          </Tooltip>
        )}
        {mail.hasAttachments && !renaming && <Attach16Regular className={styles.attach} />}
        {/* Stop clicks in the actions area from bubbling to the row's onClick
            (which opens/downloads the mail) — tapping the ⋯ menu must not
            trigger a download. */}
        <div
          className={styles.actions}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
        <Menu
          open={menuOpen}
          onOpenChange={(_e, data) => {
            setMenuOpen(data.open);
            // Cf. FolderRow : l'ancre doit mourir avec le menu, sinon le bouton ⋯
            // rouvrirait à la position du précédent clic droit.
            if (!data.open) setMenuTarget(null);
          }}
          positioning={menuPositioning(menuTarget)}
        >
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              size="small"
              icon={<MoreHorizontalRegular />}
              className={hover || menuOpen ? styles.visible : styles.hidden}
              onClick={(e) => {
                e.stopPropagation();
                setMenuTarget(null);
                setMenuOpen(true);
              }}
            />
          </MenuTrigger>
          <MenuPopover>
            {/* MENU DE LOT quand cette ligne fait partie d'une multi-sélection :
                les actions unitaires (ouvrir, renommer, presse-papiers) sont
                REMPLACÉES, pas complétées — elles n'agiraient que sur une ligne
                alors que l'utilisateur en a désigné plusieurs. */}
            <MenuList>
              {multi.isMulti && selected ? (
                multi.items
              ) : (
                <>
              <MenuItem
                icon={<ArrowDownloadRegular />}
                onClick={() => {
                  setMenuOpen(false);
                  void open();
                }}
              >
                Ouvrir / Télécharger
              </MenuItem>
              {/* Juste après « Ouvrir », et AVANT le bloc partagé : les deux
                  agissent sur le mail en tant que message, alors que la suite
                  (renommer, presse-papiers, supprimer) agit sur la fiche rangée. */}
              <MailReadMenuItem
                mailId={mail.id}
                isRead={mail.isRead}
                onDone={() => setMenuOpen(false)}
              />
              {/* Fin de menu PARTAGÉE avec le menu d'un dossier et avec la page
                  détail. « Renommer » a rejoint ce bloc : il était seul en tête
                  avec « Ouvrir », alors que c'est une modification de l'élément
                  comme le presse-papiers. */}
              <ItemActionMenuItems
                onRename={startRename}
                onCopy={() => {
                  setMenuOpen(false);
                  copyMails([
                    { mailId: mail.id, folderId: mail.folderId, containerId: mail.containerId, label },
                  ]);
                }}
                onCut={() => {
                  setMenuOpen(false);
                  cutMails([
                    { mailId: mail.id, folderId: mail.folderId, containerId: mail.containerId, label },
                  ]);
                }}
                onPaste={() => {
                  setMenuOpen(false);
                  if (mail.folderId) pasteInto(mail.folderId, mail.containerId);
                }}
                // Coller exige un dossier cible : sur un mail rangé à la racine
                // d'un projet, il n'y en a pas.
                pasteDisabled={!canPaste || isBusy || mail.folderId === null}
                pasteHint={
                  pending === 'folder'
                    ? 'Coller le dossier copié dans le dossier de ce mail'
                    : pending === 'mails'
                      ? 'Coller les mails copiés dans le dossier de ce mail'
                      : undefined
                }
                onCopyTo={() => {
                  setMenuOpen(false);
                  setTargetMode('copy');
                }}
                onMoveTo={() => {
                  setMenuOpen(false);
                  setTargetMode('move');
                }}
                onDelete={() => {
                  setMenuOpen(false);
                  setConfirmDelete(true);
                }}
              />
                </>
              )}
            </MenuList>
          </MenuPopover>
        </Menu>
        </div>
      </div>

      {/* Hors du `MenuList` : un dialogue posé dedans serait démonté avec le menu
          à la première interaction, donc ne s'afficherait jamais. */}
      {multi.dialogs}

      {targetMode && (
        <MailTargetDialog mode={targetMode} mail={mail} onClose={() => setTargetMode(null)} />
      )}

      <Dialog open={confirmDelete} onOpenChange={(_e, data) => setConfirmDelete(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Supprimer le mail ?</DialogTitle>
            <DialogContent>
              « {label} » sera supprimé (récupération possible pendant 30 jours via la corbeille).
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmDelete(false)}>
                Annuler
              </Button>
              <Button
                appearance="primary"
                disabled={deleteMut.isPending}
                onClick={() =>
                  deleteMut.mutate(
                    { mailId: mail.id, folderId: mail.folderId },
                    { onSettled: () => setConfirmDelete(false) },
                  )
                }
              >
                {deleteMut.isPending ? 'Suppression…' : 'Supprimer'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}
